const RISTA_DESIGN_CHECK_VARIANT = 63964067234169;
const RISTA_FRAME_VARIANT_NUMBERS = Object.freeze({
  none: '00',
  minimal: '01',
  klassisch: '02',
  floral: '03',
  botanisch: '04',
  'modern-line': '05',
  'art-deco': '06',
  corporate: '07',
  'rista-urkunde': '08',
  'rista-ornament': '09',
  'rista-seitenlinien': '10',
  'rista-offene-ecken': '11',
  'rista-modern-versetzt': '12',
  'rista-unterbrochen': '13',
  'rista-doppellinie-rund': '14',
  'rista-victorian': '15',
  'rista-art-nouveau': '16',
  'rista-baroque': '17',
  'rista-neo-gothic': '18',
  'rista-rococo': '19',
});

class RistaDesignerLauncher extends HTMLElement {
  connectedCallback() {
    if (this._ristaReady) return;
    this._ristaReady = true;
    this.dataset.ready = 'true';
    this.launchButton = this.querySelector('[data-rista-open-designer]');
    this.dialog = this.querySelector('[data-rista-designer-dialog]');
    this.frame = this.querySelector('[data-rista-designer-frame]');
    this.status = this.querySelector('[data-rista-designer-status]');
    this.variantMap = this.readVariantMap();
    this.designerOrigin = new URL(this.dataset.designerUrl).origin;
    const productForm = document.querySelector('product-form-component form');
    const buyButtons = productForm?.closest('.buy-buttons-block');
    if (buyButtons) {
      buyButtons.before(this);
      buyButtons.hidden = true;
    }
    this.launchButton?.addEventListener('click', () => void this.open());
    this.dialog?.addEventListener('cancel', (event) => { event.preventDefault(); this.close(); });
    window.addEventListener('message', (event) => this.onMessage(event));
    this.restoreDraft();
  }

  readVariantMap() {
    try { return JSON.parse(this.querySelector('[data-rista-variant-map]')?.textContent || '{}'); }
    catch { return {}; }
  }

  async open() {
    if (!this.dialog || !this.frame) return;
    const url = new URL(this.dataset.designerUrl);
    url.searchParams.set('parentOrigin', window.location.origin);
    url.searchParams.set('size', this.currentSize());
    url.searchParams.set('deliveryTime', this.dataset.deliveryTime?.trim() || '7–14 Tage');
    try {
      const cart = await this.readCart();
      const hasDesignCheck = await this.normalizeDesignCheck(cart);
      url.searchParams.set('designCheckInCart', hasDesignCheck ? '1' : '0');
    } catch {
      url.searchParams.set('designCheckInCart', '0');
    }
    this.frame.src = url.toString();
    this.dialog.showModal();
    document.documentElement.classList.add('rista-designer-is-open');
  }

  close() {
    this.dialog?.close();
    document.documentElement.classList.remove('rista-designer-is-open');
    this.launchButton?.focus({ preventScroll: true });
  }

  currentSize() {
    const selected = document.querySelector('product-form-component form [name="id"]')?.value;
    return this.variantMap.byVariant?.[selected]?.size || 'A5';
  }

  onMessage(event) {
    if (event.origin !== this.designerOrigin || event.source !== this.frame?.contentWindow) return;
    if (event.data?.type === 'rista:close-designer') this.close();
    if (event.data?.type === 'rista:design-approved') void this.acceptDesign(event.data);
  }

  async acceptDesign(payload) {
    const design = payload.design;
    if (!design?.approvedAt || !payload.variantPriceKey) return;
    const key = `${payload.variantPriceKey.size}:T${payload.variantPriceKey.extraTextBlocks}:I${payload.variantPriceKey.imagePackage}`;
    const variantId = this.variantMap.byKey?.[key];
    if (!variantId) {
      this.showStatus('Diese Preis-/Größenkombination ist noch nicht im Produkt angelegt.', 'error');
      return;
    }
    const form = document.querySelector('product-form-component form');
    if (!form) return;
    const variantInput = form.querySelector('[name="id"]');
    variantInput.value = String(variantId);
    variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    this.setProperty(form, '_rista_design_id', payload.designId || design.id);
    this.setProperty(form, '_rista_revision', String(payload.revision || 1));
    this.setProperty(form, '_rista_price_key', key);
    const previewUrl = payload.previewUrl ? new URL(payload.previewUrl, this.dataset.designerUrl).href : '';
    if (previewUrl) this.setProperty(form, '_rista_preview_url', previewUrl);
    if (payload.productionUrl) this.setProperty(form, '_rista_production_url', new URL(payload.productionUrl, this.dataset.designerUrl).href);
    this.syncPublicDesignProperties(form, design);
    this.setProperty(form, '_rista_design_json', JSON.stringify(design));
    sessionStorage.setItem(`rista-design:${this.dataset.productHandle}`, JSON.stringify(payload));
    this.dataset.approved = 'true';
    this.launchButton.querySelector('[data-rista-launch-label]').textContent = 'Entwurf bearbeiten';
    this.showStatus(`Entwurf freigegeben · ${this.money(payload.price.totalCents)}`, 'success');
    document.dispatchEvent(new CustomEvent('rista:design-approved', { detail: payload }));
    if (payload.commitToCart) await this.addToCart(payload, variantId);
    else this.close();
  }

  async addToCart(payload, variantId) {
    const allowedAccessories = new Set([63964061925753, 63964062351737, 63964066054521]);
    const accessories = Array.isArray(payload.accessoryIds) ? payload.accessoryIds.filter((id) => allowedAccessories.has(Number(id))).map(Number) : [];
    const groupId = payload.designId || payload.design.id;
    const properties = {
      _rista_design_id: groupId,
      _rista_revision: String(payload.revision || 1),
      _rista_price_key: `${payload.variantPriceKey.size}:T${payload.variantPriceKey.extraTextBlocks}:I${payload.variantPriceKey.imagePackage}`,
      _rista_preview_url: payload.previewUrl ? new URL(payload.previewUrl, this.dataset.designerUrl).href : '',
      _rista_production_url: payload.productionUrl ? new URL(payload.productionUrl, this.dataset.designerUrl).href : '',
      _rista_group_id: groupId,
      ...this.publicDesignProperties(payload.design),
    };
    const requiresDesignCheck = Boolean(payload.optionalDesignCheck)
      || payload.design.elements?.some((item) => item.type === 'image');
    if (requiresDesignCheck) properties._rista_requires_design_check = 'true';
    this.showStatus('Warenkorb wird vorbereitet …', 'success');
    const root = window.Shopify?.routes?.root ?? '/';
    let designCheckInCart = false;
    if (requiresDesignCheck) {
      try {
        designCheckInCart = await this.normalizeDesignCheck(await this.readCart());
      } catch { /* Falls die Abfrage scheitert, wird der erforderliche Check sicherheitshalber ergänzt. */ }
    }
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({ items: [
        { id: Number(variantId), quantity: 1, properties },
        ...accessories.map((id) => ({ id, quantity: 1, properties: { _rista_group_id: groupId, 'Gehört zu': 'Personalisiertes RISTA Board' } })),
        ...(requiresDesignCheck && !designCheckInCart ? [{
          id: RISTA_DESIGN_CHECK_VARIANT,
          quantity: 1,
          properties: { _rista_order_service: 'Profi Design-Check', 'Gilt für': 'Gesamte Bestellung' },
        }] : []),
      ] }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      this.showStatus(error.description || 'Der Warenkorb konnte nicht vorbereitet werden.', 'error');
      return;
    }
    document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { source: 'rista-designer' } }));
    window.location.assign(`${root}cart`);
  }

  async readCart() {
    const root = window.Shopify?.routes?.root ?? '/';
    const response = await fetch(`${root}cart.js`, { headers: { accept: 'application/json' }, cache: 'no-store' });
    if (!response.ok) throw new Error(`Cart HTTP ${response.status}`);
    return response.json();
  }

  async normalizeDesignCheck(cart) {
    const lines = cart.items.filter((item) => Number(item.variant_id || item.id) === RISTA_DESIGN_CHECK_VARIANT);
    if (!lines.length) return false;
    if (lines.length === 1 && Number(lines[0].quantity) === 1) return true;
    const updates = {};
    lines.forEach((item, index) => { updates[item.key] = index === 0 ? 1 : 0; });
    const root = window.Shopify?.routes?.root ?? '/';
    const response = await fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({ updates }),
    });
    if (!response.ok) throw new Error(`Cart update HTTP ${response.status}`);
    return true;
  }

  setProperty(form, key, value) {
    let input = form.querySelector(`[data-rista-property="${CSS.escape(key)}"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${key}]`;
      input.dataset.ristaProperty = key;
      form.append(input);
    }
    input.value = value;
    return input;
  }

  publicDesignProperties(design) {
    const properties = { Design: 'Kundenfreigabe erfolgt' };
    const texts = Array.isArray(design?.elements)
      ? design.elements.filter((item) => item?.type === 'text' && String(item.text || '').trim()).slice(0, 9)
      : [];
    texts.forEach((item, index) => { properties[`Text ${index + 1}`] = String(item.text).trim(); });
    properties['Größe'] = design?.size || '';
    properties.Rahmen = RISTA_FRAME_VARIANT_NUMBERS[design?.frame] || 'Unbekannt';
    return properties;
  }

  syncPublicDesignProperties(form, design) {
    form.querySelectorAll('[data-rista-public-design-property], [data-rista-property="Freigabe"], [data-rista-property="Profi Design-Check"]').forEach((input) => input.remove());
    Object.entries(this.publicDesignProperties(design)).forEach(([key, value]) => {
      const input = this.setProperty(form, key, value);
      input.dataset.ristaPublicDesignProperty = 'true';
    });
  }

  restoreDraft() {
    try {
      const payload = JSON.parse(sessionStorage.getItem(`rista-design:${this.dataset.productHandle}`));
      if (payload?.design?.approvedAt) this.acceptDesign({ ...payload, commitToCart: false });
    } catch { /* Ein beschädigter lokaler Entwurf blockiert den Kauf nicht dauerhaft. */ }
  }

  showStatus(message, state) {
    if (!this.status) return;
    this.status.textContent = message;
    this.status.dataset.state = state;
  }

  money(cents) {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cents / 100);
  }
}

if (!customElements.get('rista-designer-launcher')) customElements.define('rista-designer-launcher', RistaDesignerLauncher);
